import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, ShieldCheck, UserCheck, BookOpen, KeyRound, AlertCircle, X, Check, Loader2 } from "lucide-react";
import Button from "../../../components/common/Button";
import { AdminUserData } from "../../../services/userService";
import { useAssignUserRolesMutation } from "../../../hooks/queries/useUserAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface RoleAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUserData | null;
  onSuccess: (updatedUser: AdminUserData) => void;
}

const AVAILABLE_ROLES = [
  {
    id: "STUDENT",
    label: "Student (Learner)",
    description: "Standard tuition student accessing enrolled courses, lessons, and assignments.",
    color: "emerald",
    icon: UserCheck,
  },
  {
    id: "INSTRUCTOR",
    label: "Academic Faculty (Instructor)",
    description: "Curriculum authoring for assigned courses and tracks, syllabus management, student review.",
    color: "blue",
    icon: BookOpen,
  },
  {
    id: "ADMIN",
    label: "System Administrator",
    description: "Day-to-day operations, visitor records, task management, support tickets, and blog publishing.",
    color: "slate",
    icon: ShieldCheck,
  },
  {
    id: "SUPERADMIN",
    label: "SuperAdmin (Root Authority)",
    description: "Unrestricted platform ownership, tuition pricing, user provisioning, and financial ledgers.",
    color: "purple",
    icon: ShieldAlert,
  },
];

const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  const { addToast } = useToast();
  const [primaryRole, setPrimaryRole] = useState<string>("STUDENT");
  const [selectedRoles, setSelectedRoles] = useState<string[]>(["STUDENT"]);

  const { mutateAsync: assignRolesMutation, isPending: isSubmitting } =
    useAssignUserRolesMutation();

  useEffect(() => {
    if (user) {
      const pRole = user.role || "STUDENT";
      setPrimaryRole(pRole);
      const rolesList = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : [pRole];
      setSelectedRoles(rolesList);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;
  if (typeof document === "undefined") return null;

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles((prev) => {
      let updated: string[];
      if (prev.includes(roleId)) {
        // Prevent removing the primary role
        if (roleId === primaryRole && prev.length > 1) {
          addToast("Cannot unassign the primary active role. Select a different primary role first.", "warning");
          return prev;
        }
        updated = prev.filter((r) => r !== roleId);
        if (updated.length === 0) updated = ["STUDENT"];
      } else {
        updated = [...prev, roleId];
      }
      return updated;
    });
  };

  const handlePrimaryRoleChange = (roleId: string) => {
    setPrimaryRole(roleId);
    if (!selectedRoles.includes(roleId)) {
      setSelectedRoles((prev) => [...prev, roleId]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const res = await assignRolesMutation({
        id: user.id,
        payload: {
          primary_role: primaryRole,
          roles: selectedRoles,
        },
      });

      addToast(res.message || `Roles updated successfully for ${user.email}`, "success");
      onSuccess(res.user);
      onClose();
    } catch (err: any) {
      console.error("Failed to assign roles:", err);
      const errorMsg = err?.response?.data?.detail || "Failed to update user roles.";
      addToast(errorMsg, "error");
    }
  };

  const willHaveInstructor = selectedRoles.includes("INSTRUCTOR") || primaryRole === "INSTRUCTOR";
  const willHaveSuperAdmin = selectedRoles.includes("SUPERADMIN") || primaryRole === "SUPERADMIN";

  return createPortal(
    <AnimatePresence>
      <div
        className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6 cht-bg-slate-950/70 cht-backdrop-blur-sm cht-overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="cht-bg-white cht-rounded-[28px] cht-max-w-xl cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-my-auto"
        >
          {/* Header */}
          <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
                <KeyRound size={20} />
              </div>
              <div>
                <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                  Assign User Roles
                </h3>
                <p className="cht-text-xs cht-text-slate-500 font-medium">
                  Configuring access authority for <span className="cht-text-slate-800 font-bold">{user.name || user.email}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="cht-w-8 cht-h-8 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-700 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="cht-p-6 cht-space-y-6">
            {/* Target User Info Card */}
            <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-100 cht-flex cht-items-center cht-gap-4">
              <div className="cht-w-12 cht-h-12 cht-rounded-xl cht-bg-white cht-overflow-hidden cht-border cht-border-slate-200 cht-shrink-0">
                {user.photo_url ? (
                  <img src={user.photo_url} alt={user.name} className="cht-w-full cht-h-full cht-object-cover" />
                ) : (
                  <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-[#3e57da] cht-font-black">
                    {user.first_name ? user.first_name.charAt(0) : "U"}
                  </div>
                )}
              </div>
              <div className="cht-min-w-0 cht-flex-1">
                <h4 className="cht-text-sm cht-font-bold cht-text-slate-900 cht-truncate">
                  {user.name}
                </h4>
                <p className="cht-text-xs cht-text-slate-500 cht-truncate">{user.email}</p>
                <div className="cht-flex cht-items-center cht-gap-2 cht-mt-1">
                  <span className="cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-white cht-text-slate-600 cht-border cht-border-slate-200">
                    ID #{user.id}
                  </span>
                  <span className={`cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-md ${user.is_active ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"}`}>
                    {user.is_active ? "Active Account" : "Suspended"}
                  </span>
                </div>
              </div>
            </div>

            {/* Primary Role Selector */}
            <div className="cht-space-y-2">
              <label className="cht-text-xs cht-font-black cht-text-slate-700 cht-uppercase cht-tracking-wider">
                Primary Active Role
              </label>
              <p className="cht-text-xs cht-text-slate-400">
                Determines the default dashboard experience and initial interface on login.
              </p>
              <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-4 cht-gap-2.5 cht-pt-1">
                {AVAILABLE_ROLES.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => handlePrimaryRoleChange(role.id)}
                    className={`cht-p-3 cht-rounded-2xl cht-border cht-text-left cht-transition-all cht-flex cht-flex-col cht-items-start cht-gap-2 ${
                      primaryRole === role.id
                        ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5 cht-shadow-sm"
                        : "cht-border-slate-200 cht-bg-white hover:cht-border-slate-300"
                    }`}
                  >
                    <div className="cht-flex cht-items-center cht-justify-between cht-w-full">
                      <div className={`cht-p-1.5 cht-rounded-lg ${primaryRole === role.id ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-slate-100 cht-text-slate-600"}`}>
                        <role.icon size={16} />
                      </div>
                      <div className={`cht-w-4 cht-h-4 cht-rounded-full cht-border cht-flex cht-items-center cht-justify-center ${primaryRole === role.id ? "cht-border-[#3e57da] cht-bg-[#3e57da]" : "cht-border-slate-300"}`}>
                        {primaryRole === role.id && <div className="cht-w-1.5 cht-h-1.5 cht-rounded-full cht-bg-white" />}
                      </div>
                    </div>
                    <span className="cht-text-xs cht-font-bold cht-text-slate-800">
                      {role.id}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Multiple Roles Authorization */}
            <div className="cht-space-y-2">
              <label className="cht-text-xs cht-font-black cht-text-slate-700 cht-uppercase cht-tracking-wider">
                Authorized Secondary Roles (Multi-Role Switching)
              </label>
              <p className="cht-text-xs cht-text-slate-400">
                Allows this user to switch seamlessly between multiple role consoles without needing separate accounts.
              </p>
              <div className="cht-space-y-2 cht-pt-1">
                {AVAILABLE_ROLES.map((role) => {
                  const isChecked = selectedRoles.includes(role.id);
                  const isPrimary = primaryRole === role.id;

                  return (
                    <div
                      key={role.id}
                      onClick={() => handleRoleToggle(role.id)}
                      className={`cht-p-3.5 cht-rounded-2xl cht-border cht-cursor-pointer cht-flex cht-items-start cht-gap-3.5 cht-transition-all ${
                        isChecked
                          ? "cht-border-indigo-200 cht-bg-indigo-50/40"
                          : "cht-border-slate-200 cht-bg-white hover:cht-border-slate-300"
                      }`}
                    >
                      <div className={`cht-mt-0.5 cht-w-5 cht-h-5 cht-rounded-md cht-border cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                        isChecked ? "cht-border-[#3e57da] cht-bg-[#3e57da] cht-text-white" : "cht-border-slate-300 cht-bg-white"
                      }`}>
                        {isChecked && <Check size={13} strokeWidth={3} />}
                      </div>
                      <div className="cht-flex-1">
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <span className="cht-text-xs cht-font-bold cht-text-slate-900">
                            {role.label}
                          </span>
                          {isPrimary && (
                            <span className="cht-text-[9px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-[#3e57da] cht-text-white">
                              Primary
                            </span>
                          )}
                        </div>
                        <p className="cht-text-[11px] cht-text-slate-500 cht-mt-0.5 leading-relaxed">
                          {role.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Informational Alerts */}
            {willHaveInstructor && !user.has_instructor_profile && (
              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-blue-50/80 cht-border cht-border-blue-200 cht-flex cht-items-start cht-gap-3">
                <AlertCircle size={18} className="cht-text-[#3e57da] cht-shrink-0 cht-mt-0.5" />
                <p className="cht-text-xs cht-text-blue-900 leading-relaxed font-medium">
                  <strong>Automated Faculty Provisioning:</strong> Assigning the Instructor role will automatically create an Academic Faculty profile for this user, enabling them to be assigned to courses and career tracks.
                </p>
              </div>
            )}

            {willHaveSuperAdmin && (
              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-purple-50/80 cht-border cht-border-purple-200 cht-flex cht-items-start cht-gap-3">
                <ShieldAlert size={18} className="cht-text-purple-600 cht-shrink-0 cht-mt-0.5" />
                <p className="cht-text-xs cht-text-purple-900 leading-relaxed font-medium">
                  <strong>High Privilege Warning:</strong> Assigning SuperAdmin grants root privileges, unrestricted access to tuition pricing, user provisioning, and monthly financial ledgers.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="cht-rounded-xl"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                className="cht-rounded-xl cht-px-6"
                isLoading={isSubmitting}
              >
                Save Role Assignment
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default RoleAssignmentModal;
